import React from "react"
import { Link, PageProps, graphql } from "gatsby"

import Layout from "@/components/Layout"
import SEO from "@/components/SEO"
import Challenge from "@/components/Challenge"
import Countdown from "@/components/Countdown"
import { routes } from "@/utils/routes"
import Raised from "@/components/Pledge/Raised"
import WistiaPlayer from "@/components/WistiaPlayer"

export const pageQuery = graphql`
  query HomepageQuery {
    site {
      siteMetadata {
        title
      }
    }
  }
`

const Home: React.FC<PageProps> = ({ data }: { data: any }) => {
  // TODO: console.log({ amountRaised: data.sanityPage.amountRaised })
  function getVideoLink(position: number) {
    if (position === 2) {
      return routes.schoolProjects()
    } else if (position === 3) {
      return routes.socialMobility()
    } else {
      return routes.video({ params: { slug: `video-${position}` } })
    }
  }

  return (
    <Layout
      sectionOne={
        <>
          <div className="relative h-screen">
            <video
              id="bgvid"
              className="absolute inset-0 w-full h-full object-cover -z-50 sm:hidden"
              playsInline
              autoPlay
              muted
              loop
            >
              <source src="https://rbai-foundation.s3-eu-west-1.amazonaws.com/videos/2025/rbai_2025_loop.mp4" type="video/mp4" />
            </video>
            <video
              id="bgvid"
              className="absolute inset-0 w-full h-full object-cover -z-50 hidden sm:block"
              playsInline
              autoPlay
              muted
              loop
            >
              <source src="https://rbai-foundation.s3-eu-west-1.amazonaws.com/videos/2025/rbai_2025_loop.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-[#131312]/50 -z-40"></div>

            <div className="container text-white h-screen flex items-center px-4">
              <div className="pl-2 md:pl-6 lg:pl-10 max-w-[1000px] translate-y-[40%] md:translate-y-[35%] lg:translate-y-[30%]">
                <div className="pb-4">
                  <h1
                    className="text-left text-white text-[300%] sm:text-[400%] md:text-[531.25%] leading-[1] font-bold normal-case tracking-normal font-gilroy"
                  >
                    Enduring<br />Excellence
                  </h1>
                  <div className="mt-5 h-[8px] sm:h-[10px] w-[200px] sm:w-[320px] bg-primary"></div>
                  <h2 className="text-left text-white text-xl sm:text-2xl md:text-3xl font-gilroy font-bold mt-4">
                    Honouring Our Past, Creating Our Future
                  </h2>
                </div>
              </div>
            </div>
          </div>
        </>
      }
    >
      <SEO title="Home" />

      <div className="bg-primary w-full min-h-[120px] md:h-[84px]">
        <div className="flex flex-col h-full w-full md:grid md:grid-cols-4 md:gap-0 md:items-stretch md:min-h-[84px]">
          <a href={routes.socialMobility()} className="flex-1 flex items-center justify-center px-4 py-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[16px] leading-[24px] md:text-[19px] md:leading-[32.3px] md:border-b-0 md:border-r md:px-2 md:py-0">
            Transformative Bursaries
          </a>
          <a href={routes.southWingDevelopment()} className="flex-1 flex items-center justify-center px-4 py-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[16px] leading-[24px] md:text-[19px] md:leading-[32.3px] md:border-b-0 md:border-r md:px-2 md:py-0">
            The South Wing
          </a>
          <a href={routes.pledge()} className="flex-1 flex items-center justify-center px-4 py-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[16px] leading-[24px] md:text-[19px] md:leading-[32.3px] md:border-b-0 md:border-r md:px-2 md:py-0">
            Sponsor a Paviour
          </a>
          <a href={routes.donate()} className="flex-1 flex items-center justify-center px-4 py-4 text-center uppercase font-gilroy font-bold text-[#131312] text-[16px] leading-[24px] md:text-[19px] md:leading-[32.3px] md:px-2 md:py-0">
            Donate
          </a>
        </div>
      </div>

      <div className="bg-white w-full">
        <div className="container py-8 sm:py-14 px-4 md:px-4 lg:px-6 xl:px-0">
          <div className="flex justify-center">
            <div className="w-full">
              <h3 className="mb-4 text-xl sm:text-2xl md:text-3xl normal-case font-gilroy font-bold">
                At the formal opening of the school in 1814, one of the founders,
                Dr. William Drennan, declared that its primary purpose was '<span className="text-primary">to diffuse useful knowledge,</span> particularly among the middling orders
                of society, as one of the <span className="text-primary">necessities</span> rather than of the luxuries of life.'
              </h3>
              <div>
                <p className="mb-4 font-gilroy-light text-[18px] leading-[28.8px] tracking-[0px] text-[#6B6B6B]">
                  Over 200 years later, RBAI remains at the forefront of education in Northern Ireland, delivering an educational experience which prepares young men to fulfil their potential, regardless of race, religion or socio-economic background. Through an ambitious academic development plan, RBAI is shaping future leaders and innovators who will make a positive contribution to society.
                </p>
                <p className="mb-4 font-gilroy-light text-[18px] leading-[28.8px] tracking-[0px] text-[#6B6B6B]">
                  To ensure RBAI continues to provide a first-class educational environment in the heart of Belfast and an education which remains accessible to all, the Board of Governors is committing £20m over five years to fund a transformative bursary programme and a new South Wing for the campus, which constitutes the largest capital investment in the history of the School.
                </p>
                <p className="mb-4 font-gilroy font-bold text-[18px] leading-[28.8px] tracking-[0px] text-primary">
                  At a pivotal moment in our history, the support of alumni, parents and friends of the School will be critical, helping us raise £2.5m to fund <Link to={routes.socialMobility()} className="font-extrabold hover:underline text-primary">Transformative Bursaries</Link> and <Link to={routes.southWingDevelopment()} className="font-extrabold hover:underline text-primary">The South Wing</Link>.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
                <a href="/docs/Enduring%20Excellence.pdf" target="_blank" className="inline-flex items-start justify-start p-4 rounded-md hover:bg-gray-100">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-4 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621 0 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>

                  <div>
                    <strong className="font-gilroy">Read Our Campaign Brochure</strong>
                  </div>
                </a>

                <a href="/docs/2026 Impact Report.pdf" target="_blank" className="inline-flex items-start justify-start p-4 rounded-md hover:bg-gray-100">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-4 flex-shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621 0 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>

                  <div>
                    <strong className="font-gilroy">Read Our Latest Impact Report</strong>
                  </div>
                </a>
              </div>

              <h3 className="mt-8 mb-4 text-xl sm:text-2xl md:text-3xl normal-case font-gilroy font-bold">
                Watch The Campaign Launch Event
              </h3>

              <div className="mb-6">
                <WistiaPlayer
                  videoStyle={{
                    width: "100%",
                    height: "100%",
                  }}
                  videoID={"brhdcgkl92"}
                  className="aspect-video"
                />
              </div>

              <p className="font-gilroy-light text-[18px] leading-[28.8px] tracking-[0px] text-[#6B6B6B]">
                The launch event on 7 November 2025 saw Convenor of the RBAI Foundation, Jim Rutherford, moderate a fire side chat with Michael Howard, Chairman of the Board of Governors, Principal Janet Williamson and Conservation Architect & RBAI Governor Dawson Stelfox MBE.
                <br />
                <br />
              </p>
              <p className="font-gilroy-light text-[18px] leading-[28.8px] tracking-[0px] text-[#6B6B6B]">
                The conversation addressed the School's vision for the future, the significance of the South Wing, and the impact transformative bursaries have on the future trajectory of pupils from less advantaged backgrounds.
              </p>

            </div>
          </div>
        </div>
      </div>

    </Layout>
  )
}

export default Home
